import React, { JSX, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import SingleSelection from '../../Components/SingleSelection';
import fallback from '../../Translations/generated/product-ibe.de.json';
import Keys from '../../Translations/generated/product-ibe.de.json.keys';
import { isEqual } from 'lodash-es';
import {
  PackageDropdownFilter,
  PackageSearchParams,
  RentalCarOfficeWithDistanceWrapper,
  useApi,
  useConfig,
  useTranslation
} from '@ibe/components';
import { PackageCode, ProductIBEProps } from './PackagesList';
import {
  ApiBestPricePackageModel,
  ApiGeoUnit,
  ApiGetPostalCodeResponse,
  ApiPostalCode,
  ApiRentalCarOffice
} from '@ibe/api';
import { PromotionCodeData } from '../../Components/PromotionCode';
import { FilterParams, hasCachedValue } from './Filters';
import { Col, Container, Row } from 'reactstrap';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { BUS_SESSION_STORAGE_KEYS, SELF_ARRIVAL_CODE } from '../Util/utils';
import {
  MINI_GROUP,
  MINI_GROUP_STR_DE,
  NORMAL_GROUP,
  SMALL_GROUP,
  SMALL_GROUP_STR_DE
} from '../../Util/constant';
import { getEarliestPossibleDate } from './usePackagesListFunctions';
import CanonicalHead from '../Util/CanonicalHead';

export type PBusFilterParams = {
  numberOfTravelers: number;
  origins: string[];
  startDate: string;
  endDate: string;
  busStations: string[];
  groupSize: string;
  ownArrivalsAndDepartures: boolean;
  selectedPostalData: ApiPostalCode | undefined;
};

export type GlobalFilterParams = {
  numberOfTravelers: number;
  origins: string[];
  startDate: string;
  endDate: string;
  busStations: string[];
  groupSize?: string;
  ownArrivalsAndDepartures?: boolean;
  selectedPostalData?: ApiPostalCode;
  packageCodes?: string;
  possibleOrigins?: string[];
  earliestPossibleDate: string;
  latestPossibleDate: string;
};

const filterPropsMapper = (
  groupSize: string,
  ownArrivalsAndDepartures?: boolean,
  searchParams?: Partial<PackageSearchParams>
): PBusFilterParams => {
  return {
    numberOfTravelers: searchParams?.occupancy ? searchParams.occupancy[0].adults : 2,
    origins: searchParams?.origins?.map((origin: ApiGeoUnit) => origin.id) || [],
    startDate: searchParams?.startDate || '',
    endDate: searchParams?.endDate || '',
    busStations: ownArrivalsAndDepartures
      ? [SELF_ARRIVAL_CODE]
      : searchParams?.busStations?.map((office: ApiRentalCarOffice) => office.code) || [],
    groupSize,
    ownArrivalsAndDepartures,
    selectedPostalData: ownArrivalsAndDepartures
      ? ({ code: SELF_ARRIVAL_CODE } as ApiPostalCode)
      : searchParams?.selectedPostalData
  } as PBusFilterParams;
};

const PBusFilters = ({
  earliestPossibleDate,
  latestPossibleDate,
  onChange,
  origins,
  packageCodes,
  maintenanceMode,
  numberOfTravelers,
  packages = [],
  promotionCode,
  hasSelfArrival,
  onlySelfArrival
}: {
  earliestPossibleDate: string;
  latestPossibleDate: string;
  onChange: (filters: FilterParams, ignoreNumberOfTravelers?: boolean) => void;
  origins: string[];
  packageCodes: ProductIBEProps['packageCodes'];
  maintenanceMode?: boolean;
  numberOfTravelers: number;
  packages: ApiBestPricePackageModel[];
  promotionCode: PromotionCodeData;
  hasSelfArrival: boolean;
  onlySelfArrival: boolean;
}): JSX.Element => {
  const { t } = useTranslation('product-ibe', fallback);
  const config = useConfig();
  const api = useApi();

  type Mode = 'selectsStart' | 'selectsEnd' | 'none';
  const dateRangeRef = useRef<{ focus?: (a: Mode) => void }>(null);

  const [allPostCodes, setAllPostCodes] = useState<ApiPostalCode[]>([]);
  const [initialPostCode, setInitialPostCode] = useState<ApiPostalCode>();
  const [initialBusStations, setInitialBusStations] = useState<
    RentalCarOfficeWithDistanceWrapper[]
  >();

  const [cachedFilter] = useState<GlobalFilterParams>(
    hasCachedValue(packageCodes, earliestPossibleDate, latestPossibleDate)
  );

  const getInitialGroupSizes = (): string[] => {
    const searchParams = new URLSearchParams(window.location.search);
    const initialSizes: string[] = [];

    const hasKleingruppe =
      searchParams.has(SMALL_GROUP_STR_DE) && searchParams.get(SMALL_GROUP_STR_DE) !== 'false';
    const hasMinigruppe =
      searchParams.has(MINI_GROUP_STR_DE) && searchParams.get(MINI_GROUP_STR_DE) !== 'false';

    if (hasKleingruppe) initialSizes.push(SMALL_GROUP);
    if (hasMinigruppe) initialSizes.push(MINI_GROUP);

    if (initialSizes.length === 0 && cachedFilter?.groupSize) {
      initialSizes.push(...cachedFilter.groupSize.split(','));
    } else if (initialSizes.length === 0) {
      initialSizes.push(NORMAL_GROUP);
    }

    return initialSizes;
  };

  const [selectedGroupSizes, setSelectedGroupSizes] = useState<string[]>(getInitialGroupSizes);

  const [ownArrivalsAndDeparturesChecked, setOwnArrivalsAndDeparturesChecked] = useState<boolean>(
    cachedFilter.ownArrivalsAndDepartures ?? false
  );

  const getStoredPostalData = (): ApiPostalCode | undefined => {
    const stored = sessionStorage.getItem(BUS_SESSION_STORAGE_KEYS.selectedPostCode);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.code !== SELF_ARRIVAL_CODE) {
          return parsed;
        }
      } catch (err) {
        console.error('Could not parse stored post code', err);
      }
    }
    return undefined;
  };

  const [directPostalData] = useState<ApiPostalCode | undefined>(getStoredPostalData());

  const [packageSearchParams, setPackageSearchParams] = useState<PackageSearchParams | undefined>({
    startDate: getEarliestPossibleDate(earliestPossibleDate, config),
    endDate: latestPossibleDate,
    selectedPostalData: directPostalData
  } as PackageSearchParams);

  useEffect(() => {
    const fetchPostCodes = async () => {
      try {
        const response: ApiGetPostalCodeResponse = await api.getPostalCodes();
        setAllPostCodes(
          response.postalCodes.filter(pc => pc.latitude !== null && pc.longitude !== null)
        );
      } catch (err) {
        console.error('Error fetching postal codes:', err);
      }
    };
    fetchPostCodes();
  }, []);

  useEffect(() => {
    if (onlySelfArrival) {
      handleOwnArrivalsAndDeparturesCheck(true);
    }
  }, [onlySelfArrival]);

  const handleFilterChange = useCallback(
    (searchParams: Partial<PackageSearchParams>): void => {
      if (!isEqual(searchParams, packageSearchParams)) {
        setPackageSearchParams(searchParams);

        if (!ownArrivalsAndDeparturesChecked) {
          if (!searchParams.selectedPostalData) {
            sessionStorage.removeItem(BUS_SESSION_STORAGE_KEYS.selectedPostCode);
          } else {
            sessionStorage.setItem(
              BUS_SESSION_STORAGE_KEYS.selectedPostCode,
              JSON.stringify(searchParams.selectedPostalData)
            );
          }
        } else {
          sessionStorage.setItem(
            BUS_SESSION_STORAGE_KEYS.selectedStations,
            JSON.stringify([{ code: SELF_ARRIVAL_CODE } as RentalCarOfficeWithDistanceWrapper])
          );
        }

        // Pass array combinations joined as string
        onChange(
          filterPropsMapper(
            selectedGroupSizes.join(','),
            ownArrivalsAndDeparturesChecked,
            searchParams
          )
        );
      }
    },
    [
      onChange,
      promotionCode,
      ownArrivalsAndDeparturesChecked,
      packageSearchParams,
      selectedGroupSizes
    ]
  );

  const handleGroupSizeChecked = useCallback(
    (grpSize: string): void => {
      let updatedSizes: string[];
      if (selectedGroupSizes.includes(grpSize)) {
        updatedSizes = selectedGroupSizes.filter(size => size !== grpSize);
      } else {
        updatedSizes = [...selectedGroupSizes.filter(size => size !== NORMAL_GROUP), grpSize];
      }

      if (updatedSizes.length === 0) {
        updatedSizes = [NORMAL_GROUP];
      }

      setSelectedGroupSizes(updatedSizes);
      const combinedGroupSize = updatedSizes.join(',');
      onChange(
        filterPropsMapper(combinedGroupSize, ownArrivalsAndDeparturesChecked, packageSearchParams),
        true
      );
    },
    [
      onChange,
      promotionCode,
      packageSearchParams,
      ownArrivalsAndDeparturesChecked,
      selectedGroupSizes
    ]
  );

  const handleOwnArrivalsAndDeparturesCheck = useCallback(
    (isChecked: boolean): void => {
      const prevSelectedPostCode = sessionStorage.getItem(
        BUS_SESSION_STORAGE_KEYS.selectedPostCode
      );

      if (prevSelectedPostCode) {
        try {
          const parsedPostCode: ApiPostalCode = JSON.parse(prevSelectedPostCode);
          if (parsedPostCode.code !== SELF_ARRIVAL_CODE) {
            setInitialPostCode(parsedPostCode);
          }
        } catch (err) {
          console.error(err);
        }
      }

      const prevSelectedBusStations = sessionStorage.getItem(
        BUS_SESSION_STORAGE_KEYS.selectedStations
      );

      if (prevSelectedBusStations) {
        try {
          const parsedBusStations: RentalCarOfficeWithDistanceWrapper[] = JSON.parse(
            prevSelectedBusStations
          );
          if (parsedBusStations.some(station => station.code !== SELF_ARRIVAL_CODE)) {
            setInitialBusStations(parsedBusStations);
          }
        } catch (err) {
          console.error(err);
        }
      }

      if (isChecked === ownArrivalsAndDeparturesChecked) return;

      setOwnArrivalsAndDeparturesChecked(isChecked);

      const updatedPostCode = isChecked
        ? ({ code: SELF_ARRIVAL_CODE } as ApiPostalCode)
        : initialPostCode;
      const updateBusStations = isChecked
        ? [{ code: SELF_ARRIVAL_CODE } as RentalCarOfficeWithDistanceWrapper]
        : initialBusStations;

      setPackageSearchParams(prev => ({
        ...prev,
        selectedPostalData: updatedPostCode
      }));

      updateStorage(updatedPostCode, updateBusStations);

      const combinedGroupSize = selectedGroupSizes.join(',');

      const updatedParams = filterPropsMapper(combinedGroupSize, isChecked, {
        ...packageSearchParams,
        selectedPostalData: updatedPostCode
      });
      onChange(updatedParams, true);

      if (!isChecked) {
        const { selectedPostalData, ...reversedParams } = filterPropsMapper(
          combinedGroupSize,
          false,
          {
            ...packageSearchParams,
            selectedPostalData: updatedPostCode
          }
        );
        onChange(reversedParams, true);
      }
    },
    [
      onChange,
      ownArrivalsAndDeparturesChecked,
      initialPostCode,
      initialBusStations,
      packageSearchParams,
      selectedGroupSizes
    ]
  );

  const updateStorage = (
    updatedPostCode?: ApiPostalCode,
    updateBusStations?: RentalCarOfficeWithDistanceWrapper[]
  ) => {
    sessionStorage.setItem(
      BUS_SESSION_STORAGE_KEYS.selectedPostCode,
      updatedPostCode ? JSON.stringify(updatedPostCode) : ''
    );

    sessionStorage.setItem(
      BUS_SESSION_STORAGE_KEYS.selectedStations,
      updateBusStations ? JSON.stringify(updateBusStations) : ''
    );
  };

  const onToggle = useCallback(
    ({ isOpen, endDate, startDate }: { isOpen?: boolean; endDate?: Date; startDate?: Date }) => {
      if (isOpen && !endDate && startDate && dateRangeRef && dateRangeRef.current?.focus) {
        dateRangeRef.current?.focus('selectsEnd');
      }
    },
    [dateRangeRef]
  );

  const checkForGroupSize = (grpSize: string) => {
    return (
      packageCodes.some(code => code.groupSize === grpSize) &&
      !packageCodes.every(code => code.groupSize === grpSize) &&
      (packageCodes.reduce(
        (total: string[], current: PackageCode) =>
          !total.includes(current.code) ? [...total, current.code] : [...total],
        []
      ).length > 1 ||
        !packageCodes.some(code => code.groupSize === grpSize)) &&
      packages.some(singlePackage => singlePackage.groupSize === grpSize)
    );
  };

  return (
    <>
      <CanonicalHead />
      {(checkForGroupSize(SMALL_GROUP) ||
        packageCodes.some(code => code.groupSize === SMALL_GROUP)) &&
      !maintenanceMode ? (
        <SingleSelection
          selectionId={'small-group-selection'}
          groupSizeChecked={selectedGroupSizes.includes(SMALL_GROUP)}
          setIsChecked={() => handleGroupSizeChecked(SMALL_GROUP)}
          groupSize={SMALL_GROUP}
          promotionCode={promotionCode}
          showInfoTextInFront={true}
        />
      ) : (
        <></>
      )}
      {(checkForGroupSize(MINI_GROUP) ||
        packageCodes.some(code => code.groupSize === MINI_GROUP)) &&
      !maintenanceMode ? (
        <SingleSelection
          selectionId={'mini-group-selection'}
          groupSizeChecked={selectedGroupSizes.includes(MINI_GROUP)}
          setIsChecked={() => handleGroupSizeChecked(MINI_GROUP)}
          groupSize={MINI_GROUP}
          promotionCode={promotionCode}
          showInfoTextInFront={true}
        />
      ) : (
        <></>
      )}
      <div className={'all-filter-container'}>
        <PackageDropdownFilter
          packageCodes={packageCodes}
          onChange={handleFilterChange}
          datepickerMaxDate={latestPossibleDate}
          datepickerMinDate={earliestPossibleDate}
          origins={cachedFilter.possibleOrigins || origins}
          ownArrivalsAndDepartures={ownArrivalsAndDeparturesChecked}
          disabled={maintenanceMode}
          defaultDate={{
            startDate: getEarliestPossibleDate(
              cachedFilter.startDate || earliestPossibleDate,
              config
            ),
            endDate: latestPossibleDate
          }}
          onToggle={{
            calendar: onToggle
          }}
          dateRangeRef={dateRangeRef as RefObject<{ focus?: (a: Mode) => void }>}
          numberOfTravelers={numberOfTravelers}
          memoDeps={[promotionCode, ownArrivalsAndDeparturesChecked]}
          showNumberSelector={false}
          isBusPackage={true}
          preSelectedBusStations={cachedFilter.busStations}
          preSelectedPostalData={packageSearchParams?.selectedPostalData}
          allPostCodes={allPostCodes}
          onlySelfArrival={onlySelfArrival}
        />
        <Container className={'single-selections-container'}>
          <Row>
            {hasSelfArrival && (
              <Col>
                <SingleSelection
                  selectionId={'arrAndDep'}
                  groupSizeChecked={ownArrivalsAndDeparturesChecked}
                  setIsChecked={onlySelfArrival ? () => {} : handleOwnArrivalsAndDeparturesCheck}
                  checkboxLabel={
                    onlySelfArrival ? t(Keys.onlySelfArrivalPossible) : t(Keys.busStops)
                  }
                  icon={onlySelfArrival ? undefined : faInfoCircle}
                  promotionCode={promotionCode}
                  showTooltip={!onlySelfArrival}
                  tooltipText={t(Keys.busStopsTooltip)}
                  showInfoTextInFront={false}
                />
              </Col>
            )}
          </Row>
        </Container>
      </div>
    </>
  );
};

export default PBusFilters;
